import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Target, Lock, Search, RefreshCw, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One target occupation, everywhere. Pages no longer own a career text box, they read the
 * locked target and, if the person wants to look at something else, they preview it without
 * changing what the rest of the platform is calculating against.
 */
export const CareerTargetLock = ({ onPreview, previewing, allowPreview = true, className = '' }) => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/career/target`, getAuthHeader());
      setData(r.data);
    } catch { /* target simply isn't set yet */ }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const search = async (term) => {
    setQ(term);
    if (term.trim().length < 2) { setResults([]); return; }
    const r = await axios.get(`${API}/career/occupations?q=${encodeURIComponent(term)}`, getAuthHeader());
    setResults(r.data?.occupations || []);
  };

  const propose = async (title, confirm = false) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/career/target`, { title, confirm }, getAuthHeader());
      if (r.data?.requires_confirmation) { setProposal(r.data); return; }
      setProposal(null); setOpen(false); setQ(''); setResults([]);
      await load();
      toast.success(r.data?.message || 'Target locked');
      if (r.data?.changed) window.setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not set that target'));
    } finally {
      setSaving(false);
    }
  };

  const target = data?.target;

  return (
    <div className={`rounded-xl border border-cyan-500/25 bg-slate-900/60 px-4 py-3 ${className}`}
      style={{ boxShadow: '0 0 30px -20px rgba(34,211,238,0.6)' }} data-testid="career-target-lock">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Target className="w-4 h-4 text-cyan-300 shrink-0" />
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-300">My target</span>
        {target ? (
          <>
            <span className="text-sm font-semibold text-white" data-testid="career-target-title">{target.title}</span>
            {target.soc_code && <span className="text-xs text-slate-300">SOC {target.soc_code}</span>}
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
              <Lock className="w-3 h-3" /> locked
            </span>
          </>
        ) : (
          <span className="text-sm text-slate-300" data-testid="career-target-empty">
            Not set, everything below is generic until you pick one
          </span>
        )}
        <button onClick={() => setOpen(true)} data-testid="career-target-change"
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-100 transition-colors">
          <RefreshCw className="w-3 h-3" /> {target ? 'Change target' : 'Set my target'}
        </button>
      </div>

      {previewing && previewing !== target?.title && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="career-target-previewing">
          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-200">
            Previewing {previewing}
          </span>
          <span className="text-slate-300">- nothing else on the platform has changed.</span>
          <button onClick={() => propose(previewing)} className="text-cyan-300 hover:text-cyan-100"
            data-testid="career-target-adopt-preview">Make this my target</button>
          <button onClick={() => onPreview?.('')} className="text-slate-300 hover:text-white"
            data-testid="career-target-clear-preview">Back to my target</button>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setProposal(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {proposal ? 'Changing your target recalculates your FuturePath' : 'Which occupation are you aiming at?'}
            </DialogTitle>
          </DialogHeader>

          {!proposal ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-300">
                Only verified occupations can be priced and projected, so pick the closest real title.
                This is the one answer every page reads.
              </p>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={q} onChange={e => search(e.target.value)} className="pl-9"
                  placeholder="Search verified occupations" data-testid="career-target-search" />
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-800 rounded-lg border border-slate-800">
                {results.map(o => (
                  <div key={o.soc_code + o.title} className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{o.title}</p>
                      <p className="text-[11px] text-slate-300">{o.field} · SOC {o.soc_code}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-2 shrink-0">
                      {allowPreview && onPreview && (
                        <button onClick={() => { onPreview(o.title); setOpen(false); }}
                          className="text-[11px] text-slate-300 hover:text-white px-2 py-1 rounded-full border border-slate-700"
                          data-testid={`career-target-preview-${o.soc_code}`}>Just look</button>
                      )}
                      <Button size="sm" disabled={saving} onClick={() => propose(o.title)}
                        data-testid={`career-target-pick-${o.soc_code}`}>Make it my target</Button>
                    </div>
                  </div>
                ))}
                {q.trim().length >= 2 && results.length === 0 && (
                  <p className="px-3 py-4 text-sm text-slate-300">No verified occupation matches that.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <span className="text-slate-300 line-through">{proposal.current?.title}</span>
                <span className="text-cyan-300">→</span>
                <span className="text-white font-semibold">{proposal.proposed?.title}</span>
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-100">{proposal.message}</p>
              </div>
              <div className="space-y-1.5" data-testid="career-target-impact">
                {(proposal.impact || []).map((i, n) => (
                  <div key={n} className="flex items-start gap-2 text-xs">
                    <Check className="w-3.5 h-3.5 text-cyan-300 mt-0.5 shrink-0" />
                    <span className="text-slate-300"><span className="text-white font-medium">{i.module}</span>, {i.what}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-300 shrink-0">{i.action}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Button disabled={saving} onClick={() => propose(proposal.proposed.title, true)}
                  data-testid="career-target-confirm">Change it and recalculate</Button>
                <Button variant="outline" onClick={() => setProposal(null)}
                  data-testid="career-target-cancel">Keep {proposal.current?.title}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareerTargetLock;
